import React from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

export interface TextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> {
    label?: string;
    registration?: UseFormRegisterReturn;
    error?: string;
    rows?: number;
    required?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ label, registration, error, id, rows = 4, required, className = '', ...rest }, ref) => {
        const textareaId = id ?? registration?.name ?? label?.toLowerCase().replace(/\s+/g, '-');

        return (
            <div className="mb-[8px] 2xl:mb-[12px] w-full">

                {label && (
                    <label htmlFor={textareaId} className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] mb-1">
                        {label}
                        {required && <span className="text-[var(--semantic-error-color)] ml-0.5">*</span>}
                    </label>
                )}

                <textarea
                    id={textareaId}
                    rows={rows}
                    className={[
                        'w-full rounded-md border px-3 py-[6px] 2xl:py-[9px] text-[12px] 2xl:text-[13px] text-[var(--secondary-color)] bg-[var(--primary-canvas-color)]',
                        'placeholder:text-[var(--disabled-text-color)] outline-none resize-none',
                        'transition duration-150',
                        error
                            ? 'border-[var(--semantic-error-color)] focus:border-[var(--semantic-error-color)]'
                            : 'border-[var(--neutral-color)] focus:border-[var(--primary-color)]',
                        'disabled:bg-gray-100 disabled:cursor-not-allowed',
                        className,
                    ].filter(Boolean).join(' ')}
                    {...registration}
                    ref={(node) => {
                        if (registration?.ref) registration.ref(node);
                        if (typeof ref === 'function') ref(node);
                        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
                    }}
                    {...rest}
                />

                {error && (
                    <span className="text-[11px] text-[var(--semantic-error-color)]" role="alert">
                        {error}
                    </span>
                )}
            </div>
        );
    },
);

Textarea.displayName = 'Textarea';

export default Textarea;
